import { useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  CATEGORIES,
  FontAwesomeIcon,
} from '@hivebrite/design-system/FontAwesomeIcon';
import {
  UserTooltip,
  type UserTooltipProps,
} from '@hivebrite/design-system/UserTooltip';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { useShouldDisplayInternalMessagingAction } from 'hb-react/frontoffice/utils/messaging';
import useGetUserInformation from 'hb-react/shared/components/UserSnapshot/hooks/useGetUserInformation';
import { UserIntroduction } from 'hb-react/shared/components/UserSnapshot/UserIntroduction';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';

interface UserSnapshotContentProps {
  children: React.ReactNode;
  userId: number;
}

const UserSnapshotContent = ({
  children,
  userId,
}: UserSnapshotContentProps) => {
  const { currentUser } = useDefaultAppContexts();
  const navigate = useNavigate();
  const { flagUjAiUserProfileSummary } = useFeatureFlag();
  const [isOpen, setIsOpen] = useState(false);

  const {
    data: user,
    refetch,
    isLoading,
    isStale,
    isError,
  } = useGetUserInformation(userId);

  const isOwnProfile = user?.id === currentUser?.id;

  const displayMessagingButton = useShouldDisplayInternalMessagingAction({
    user: { id: userId },
    enabled: isOpen,
  });

  const navigateToProfile = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      navigate(Routes.user_path(userId));
    },
    [navigate, userId],
  );

  const navigateToMessage = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!userId) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      navigate(
        Routes.messaging_new_conversation_path({ recipient_ids: userId }),
      );
    },
    [navigate, userId],
  );

  const buttons = useMemo((): UserTooltipProps['buttons'] => {
    if (isOwnProfile) {
      return {
        secondary: {
          dataTestId: 'user-snapshot-edit-profile-button',
          icon: () => (
            <FontAwesomeIcon
              height="16px"
              iconName="pen"
              iconType={CATEGORIES.SOLID}
            />
          ),
          children: __('Edit Profile'),
          onClick: navigateToProfile,
        },
      };
    }

    return {
      primary: displayMessagingButton
        ? {
            dataTestId: 'user-snapshot-message-button',
            icon: () => (
              <FontAwesomeIcon
                height="16px"
                iconName="paper-plane"
                iconType={CATEGORIES.SOLID}
              />
            ),
            children: __('Message'),
            onClick: navigateToMessage,
          }
        : undefined,
      secondary: {
        dataTestId: 'user-snapshot-view-profile-button',
        icon: () => (
          <FontAwesomeIcon
            height="16px"
            iconName="user"
            iconType={CATEGORIES.SOLID}
          />
        ),
        children: __('View Profile'),
        onClick: navigateToProfile,
      },
    };
  }, [
    isOwnProfile,
    displayMessagingButton,
    navigateToProfile,
    navigateToMessage,
  ]);

  const onOpen = () => {
    if (!user || isStale) {
      refetch();
    }
    setIsOpen(true);
  };

  const introduction =
    flagUjAiUserProfileSummary &&
    user &&
    (user.introduction || isOwnProfile) ? (
      <UserIntroduction
        user={user}
        userId={userId}
        isOwnProfile={isOwnProfile}
      />
    ) : null;

  // Return children without tooltip if there's an error
  if (isError) {
    return children;
  }

  return (
    <UserTooltip
      buttons={buttons}
      user={user}
      content={introduction}
      onOpen={onOpen}
      isLoading={isLoading}
    >
      {children}
    </UserTooltip>
  );
};

interface UserSnapshotProps {
  children: React.ReactNode;
  userId: number;
}

const UserSnapshot = ({ children, userId }: UserSnapshotProps) => {
  const { flagUjUserProfileSnapshot } = useFeatureFlag();
  const { currentUser } = useDefaultAppContexts();
  const isFO = !!currentUser;

  // Return children without tooltip if feature flag is disabled
  if (!isFO || !flagUjUserProfileSnapshot) {
    return children;
  }

  return <UserSnapshotContent userId={userId}>{children}</UserSnapshotContent>;
};

export default UserSnapshot;
